import { Injectable } from '@angular/core';
import * as appboy from '@braze/web-sdk';
import { BehaviorSubject, interval, merge, switchMap, tap, withLatestFrom } from 'rxjs';
import { Card, Genders, User } from '@braze/web-sdk';
import { environment } from '../../environments/environment';
import { CustomerAuth } from '../../smoothr-web-app-core/models/CustomerAuth';
import { Gender } from '../../smoothr-web-app-core/enums/Gender';
import { CookieConsentService } from './cookie.service';
import moment from 'moment';
import { RepositoryService } from '../../smoothr-web-app-core/services/repository/repository.service';
import { filter } from 'rxjs/operators';
import Customer from '../../smoothr-web-app-core/models/Customer';
import { HttpClient } from '@angular/common/http';

export enum BrazeEvents {
	open_category = 'open_category',
	open_article = 'open_article',
	article_selected = 'article_selected',
	open_cart = 'open_cart',
	add_more = 'add_more',
	edit_article = 'edit_article',
	remove_article = 'remove_article',
	increase_article_count = 'increase_article_count',
	decrease_article_count = 'decrease_article_count',
	add_promo = 'add_promo',
	make_payment = 'make_payment',
}

@Injectable({
	providedIn: 'root',
})
export class BrazeService {
	contentCards$ = new BehaviorSubject<Card[]>([]);
	truckUserEvents = false;
	currUser: Customer;

	eventsArray: BehaviorSubject<{ email: string; name: string; time: string }[]> = new BehaviorSubject([]);

	constructor(private http: HttpClient) {
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'hidden') this.sendBrazeEventsChunk();
		});
	}

	public disableSdk() {
		if (appboy.isInitialized()) {
			appboy.disableSDK();
		}
	}

	public initializeService(uid?: string) {
		if (appboy.isInitialized()) {
			return;
		}
		if (!uid) {
			console.warn('[BrazeDebug] initializeService: called without uid, aborting to prevent anonymous profile creation');
			return;
		}
		appboy.initialize(environment.braze.apiKey, {
			baseUrl: environment.braze.url,
			allowUserSuppliedJavascript: true,
			enableLogging: !environment.production,
			doNotLoadFontAwesome: true,
		});
		appboy.automaticallyShowInAppMessages();

		appboy.subscribeToContentCardsUpdates(cards => {
			this.contentCards$.next(cards.cards as any[]);
		});
		appboy.subscribeToInAppMessage(inAppMessage => {});

		appboy.subscribeToSdkAuthenticationFailures(error => {
			console.error('SdkAuthenticationFailures', error);
		});

		appboy.changeUser(uid);
		const sessionUser = appboy.getUser();
		if (sessionUser) {
			sessionUser.setCustomUserAttribute('web_app_version', environment.appVersion);
			sessionUser.setCustomUserAttribute('init_page', window.location.pathname);
		}
		appboy.openSession();
		appboy.requestImmediateDataFlush();
		this.initEventsBatchSender();
	}

	public isInitialized(): boolean {
		return appboy.isInitialized();
	}

	public isPushPermissionGranted() {
		return appboy.isPushPermissionGranted();
	}

	public checkIsSupported() {
		return appboy.isPushSupported();
	}

	public logCustomEvent(eventName: BrazeEvents, properties: any = {}) {
		console.log(
			'[BrazeDebug] logCustomEvent:',
			eventName,
			'| marketing:',
			CookieConsentService.marketing,
			'| truckUserEvents:',
			this.truckUserEvents,
			'| currUser:',
			this.currUser?.uid,
			'| isAnonymous:',
			this.currUser?.isAnonymous
		);
		if (!CookieConsentService.marketing || !this.truckUserEvents) return;

		if (!this.currUser.isAnonymous) appboy.logCustomEvent(eventName, properties);
		else {
			const val = this.eventsArray.value;
			val.push({ email: this.currUser.email, name: eventName, time: new Date().toISOString() });
			this.eventsArray.next(val);
		}
	}

	initUser(user: CustomerAuth) {
		console.log(
			'[BrazeDebug] initUser called | uid:',
			user?.customer?.uid,
			'| isAnonymous:',
			user?.customer?.isAnonymous,
			'| email:',
			user?.customer?.email,
			'| name:',
			user?.customer?.name,
			'| sdkInitialized:',
			appboy.isInitialized(),
			'| caller:',
			new Error().stack?.split('\n')[2]?.trim()
		);
		if (!appboy.isInitialized()) {
			console.warn('[BrazeDebug] initUser: SDK not initialized, returning early — user will NOT be tracked');
			return;
		}
		this.truckUserEvents = true;
		appboy.changeUser(user.customer.uid);
		this.currUser = user.customer;
		this.setUserAttributes(user.customer);
	}

	public setUserAttributes(customer: Customer) {
		const brazeUser = appboy.getUser();
		if (!brazeUser) return;
		if (customer.email) brazeUser.setEmail(customer.email);
		if (customer.firstName) brazeUser.setFirstName(customer.firstName);
		if (customer.lastName) brazeUser.setLastName(customer.lastName);
		if (customer.phone) brazeUser.setPhoneNumber(customer.phone);
		brazeUser.setCustomUserAttribute('web_app_version', environment.appVersion);
		brazeUser.setCustomUserAttribute('last_active_page', window.location.pathname);
	}

	public initForGuestSubscription(customer: Customer) {
		if (!CookieConsentService.marketing) return;
		// Anonymous users must NOT create a Braze profile via changeUser() — doing so creates
		// duplicate profiles when a guest email matches an existing registered account.
		// Their events are routed through the backend batch sender which handles attribution by email.
		if (!customer.isAnonymous) {
			if (!appboy.isInitialized()) {
				this.initializeService(customer.uid);
			} else {
				appboy.changeUser(customer.uid);
			}
			this.setUserAttributes(customer);
		}
		this.truckUserEvents = true;
		this.currUser = customer;
	}

	requestPushPermission() {
		appboy.requestPushPermission(
			(enpoint, publickey, userAuth) => {},
			denied => {
				console.error('Push notification permission error:', denied);
			}
		);
	}

	onCardClick(card: any): void {
		try {
			//appboy.logCardClick(card);

			appboy.logContentCardImpressions([card]);
		} catch (e) {
			console.error(e);
		}
	}

	initEventsBatchSender() {
		const minuteTrigger$ = interval(30_000);
		const sizeTrigger$ = this.eventsArray.pipe(filter(arr => arr.length >= 70));

		merge(minuteTrigger$, sizeTrigger$)
			.pipe(
				withLatestFrom(this.eventsArray),
				filter(([_, arr]) => arr.length > 0),
				switchMap(([_, arr]) => {
					return this.sendBrazeEventsChunk();
				})
			)
			.subscribe();
	}

	public async sendBrazeEventsChunk() {
		if (!this.eventsArray.value.length) return;

		const res = fetch(environment.baseUrl + '/v1/braze/track/user/events', {
			method: 'POST',
			body: JSON.stringify(this.eventsArray.value),
			headers: { 'Content-Type': 'application/json' },
			keepalive: true,
		});
		this.eventsArray.next([]);
		return res;
	}
}
